import realStarData from "../data/real-stars.json";
import type { PlanetData, StarSystem } from "../types";
import { celestialToSpatial } from "../types";

// ---------------------------------------------------------------------------
// Solar System — origin of the spatial map
// ---------------------------------------------------------------------------

const solarSystem: StarSystem = {
	id: "sol",
	name: "Solar System",
	x: 0,
	y: 0,
	zone: 0,
	systemIndex: 0,
	star: {
		name: "Sun",
		spectralClass: "G2V",
		apparentMagnitude: -26.74,
		absoluteMagnitude: 4.83,
		distanceLightYears: 0,
		stellarTemp: 5778,
		stellarMass: 1.0,
		stellarRadius: 1.0,
	},
	planets: [
		{
			id: "sol-p1",
			name: "Mercury",
			orbitalRadius: 0.39,
			orbitalPeriod: 88,
			radius: 0.38,
			color: "#b0b0b0",
		},
		{
			id: "sol-p2",
			name: "Venus",
			orbitalRadius: 0.72,
			orbitalPeriod: 225,
			radius: 0.95,
			color: "#e8cda0",
		},
		{
			id: "sol-p3",
			name: "Earth",
			orbitalRadius: 1.0,
			orbitalPeriod: 365,
			radius: 1.0,
			color: "#4da6ff",
		},
		{
			id: "sol-p4",
			name: "Mars",
			orbitalRadius: 1.52,
			orbitalPeriod: 687,
			radius: 0.53,
			color: "#c1440e",
		},
		{
			id: "sol-p5",
			name: "Jupiter",
			orbitalRadius: 5.2,
			orbitalPeriod: 4333,
			radius: 11.21,
			color: "#c88b3a",
		},
		{
			id: "sol-p6",
			name: "Saturn",
			orbitalRadius: 9.54,
			orbitalPeriod: 10759,
			radius: 9.45,
			color: "#d4b96e",
		},
		{
			id: "sol-p7",
			name: "Uranus",
			orbitalRadius: 19.19,
			orbitalPeriod: 30687,
			radius: 4.01,
			color: "#72b5c4",
		},
		{
			id: "sol-p8",
			name: "Neptune",
			orbitalRadius: 30.07,
			orbitalPeriod: 60190,
			radius: 3.88,
			color: "#3f54ba",
		},
	],
};

// ---------------------------------------------------------------------------
// Zone computation — 30 zones (0-29) using equal-distribution boundaries
// ---------------------------------------------------------------------------

const MAX_ZONE = 29;

/** Zone boundary distances (light-years). Zone N starts at ZONE_BOUNDARIES[N]. */
const ZONE_BOUNDARIES: number[] = realStarData.meta.zoneBoundaries;

/** Map distance in light-years to zone 0-29 (equal-distribution bucketing). */
export function distanceToZone(distanceLy: number): number {
	if (distanceLy <= 0) return 0;
	// Binary search for the zone
	let lo = 0;
	let hi = MAX_ZONE;
	while (lo < hi) {
		const mid = (lo + hi + 1) >> 1;
		if ((ZONE_BOUNDARIES[mid] ?? Number.POSITIVE_INFINITY) <= distanceLy) {
			lo = mid;
		} else {
			hi = mid - 1;
		}
	}
	return lo;
}

// ---------------------------------------------------------------------------
// Build star systems from real NASA Exoplanet Archive data
// ---------------------------------------------------------------------------

function buildRealSystems(): StarSystem[] {
	return realStarData.systems.map((sys) => {
		const { x, y } = celestialToSpatial(
			sys.ra / 15, // RA in degrees → hours (divide by 15)
			sys.dec,
			sys.star.distanceLightYears,
		);

		const planets: PlanetData[] = sys.planets.map((p) => ({
			id: p.id,
			name: p.name,
			orbitalRadius: p.orbitalRadius,
			orbitalPeriod: p.orbitalPeriod,
			radius: p.radius,
			mass: p.mass,
			discoveryMethod: p.discoveryMethod,
			color: p.color,
			equilibriumTemp: p.equilibriumTemp,
			eccentricity: p.eccentricity,
		}));

		return {
			id: sys.id,
			name: sys.name,
			x,
			y,
			zone: sys.zone,
			systemIndex: sys.systemIndex,
			star: {
				name: sys.star.name,
				spectralClass: sys.star.spectralClass,
				apparentMagnitude: sys.star.apparentMagnitude,
				absoluteMagnitude: sys.star.absoluteMagnitude,
				distanceLightYears: sys.star.distanceLightYears,
				stellarTemp: sys.star.stellarTemp,
				stellarMass: sys.star.stellarMass,
				stellarRadius: sys.star.stellarRadius,
			},
			planets,
		};
	});
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const STAR_SYSTEMS: StarSystem[] = [solarSystem, ...buildRealSystems()];

export async function fetchStarSystems(): Promise<StarSystem[]> {
	await new Promise((resolve) => setTimeout(resolve, 300));
	return STAR_SYSTEMS;
}
